import { axiosPlacement } from "./AxiosInstance.js";

// ✅ Search placements with filters (POST request) - This is the main API
export const searchPlacements = async (payload) => {
  try {
    const response = await axiosPlacement.post(`/jobprofiles/searchProfiles`, payload);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to search placements: ${error.message}`);
  }
};

// Cache for placement requests to prevent duplicates
const _placementCache = new Map();
const _appliedStudentsCache = new Map();

// ✅ Get single placement by ID (GET request)
export const getPlacementById = async (id) => {
  if (!id) return null;
  
  // Use promise-level cache to prevent simultaneous duplicate requests
  if (_placementCache.has(id)) {
    return _placementCache.get(id);
  }

  const promise = axiosPlacement.get(`/jobprofiles/${id}`).then(response => {
    // Clear cache after a short delay to allow future refreshes but block concurrent ones
    setTimeout(() => _placementCache.delete(id), 5000);
    return response.data;
  }).catch(error => {
    _placementCache.delete(id);
    throw new Error(`Failed to fetch placement: ${error.message}`);
  });

  _placementCache.set(id, promise);
  return promise;
};

// ✅ Create new placement
export const createPlacement = async (placementData) => {
  try {
    const response = await axiosPlacement.post(`/jobprofiles`, placementData);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to create placement: ${error.message}`);
  }
};

// ✅ Update placement (PUT - partial update)
export const updatePlacement = async (id, updatedFields) => {
  try {
    const response = await axiosPlacement.put(`/jobprofiles/${id}`, updatedFields);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to update placement: ${error.message}`);
  }
};

// ✅ Replace entire placement (PUT - full update)
export const replacePlacement = async (id, newPlacementData) => {
  try {
    const response = await axiosPlacement.put(`/jobprofiles/${id}`, newPlacementData);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to replace placement: ${error.message}`);
  }
};

// ✅ Delete placement
export const deletePlacement = async (id) => {
  try {
    await axiosPlacement.delete(`/jobprofiles/${id}`);
    return id;
  } catch (error) {
    throw new Error(`Failed to delete placement: ${error.message}`);
  }
};

// ✅ Get applied students for a particular job with pagination
export const getAppliedStudentsForJob = async (id, page = 1, limit = 20) => {
  if (!id) return null;
  
  const cacheKey = `${id}_${page}_${limit}`;
  if (_appliedStudentsCache.has(cacheKey)) {
    return _appliedStudentsCache.get(cacheKey);
  }

  const promise = axiosPlacement.get(`/jobprofiles/applied-students`, {
    params: { id, page, limit }
  }).then(response => {
    setTimeout(() => _appliedStudentsCache.delete(cacheKey), 5000);
    return response.data;
  }).catch(error => {
    _appliedStudentsCache.delete(cacheKey);
    throw new Error(`Failed to fetch applied students for job: ${error.message}`);
  });

  _appliedStudentsCache.set(cacheKey, promise);
  return promise;
};

// ✅ Get detailed posted job
export const getDetailedPostedJob = async (id) => {
  try {
    const response = await axiosPlacement.get(`/jobprofiles/detailed-posted-job?id=${id}`);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to fetch detailed posted job: ${error.message}`);
  }
};

// ✅ Get placement form options
export const getPlacementOptions = async () => {
  try {
    const response = await axiosPlacement.get(`/placementOptions`);
    const data = response.data;

    const convertToOptions = (str) => {
      if (!str) return [];
      return str.split(',').map(item => ({
        value: item.trim(),
        label: item.trim()
      }));
    };

    return {
      assignedTo: convertToOptions(data.assignedTo),
      departments: convertToOptions(data.departments),
      salutations: convertToOptions(data.salutations),
      jobPositionStatus: convertToOptions(data.jobPositionStatus),
      courses: convertToOptions(data.courses)
    };
  } catch (error) {
    throw new Error(`Failed to fetch placement options: ${error.message}`);
  }
};

// ✅ Get metrics for dashboard
export const getMetrics = async () => {
  try {
    const response = await axiosPlacement.get(`/metrics`);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to fetch metrics: ${error.message}`);
  }
};

// ✅ Get students filtered by courseIds or batchIds (POST)
export const getCourseBatchStudents = async (payload) => {
  try {
    const response = await axiosPlacement.post(`/jobprofiles/course-batch-students`, payload);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to fetch course/batch students: ${error.message}`);
  }
};

// Note: getPlacements is kept for backward compatibility but not used
export const getPlacements = async (limit = 50000, page = 1, search = "") => {
  try {
    const response = await axiosPlacement.get(`/jobprofiles`, {
      params: { limit, page, search }
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to fetch placements: ${error.message}`);
  }
};